/* eslint-disable @typescript-eslint/no-explicit-any */
import { DATA_ATTRIBUTES, ROOT_ID, UC_UI_GLOBAL_NAMESPACE } from './constants';
import { FunctionalComponent, h, render } from 'preact';
import { InitParameters, getInitParameters } from './modules/helpers';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import Usercentrics, {
  BaseService,
  CONSENT_TYPE,
  InitialUIValues,
  Service,
  Settings,
  TCFVendor,
  UserCountryData,
  UserSessionData,
} from '@usercentrics/cmp-browser-sdk';
import { useEffect, useState } from 'preact/hooks';

import App from './App';
import { CategoryContextProvider } from './contexts/CategoryContext';
import { CustomCssProvider } from './contexts/CustomCss';
import { ERROR_MESSAGES } from './enums';
import { GlobalStateContextProvider } from './contexts/GlobalContext';
import GlobalStyles from './components/GlobalStyles';
import LightTheme from './styles';
import { SettingsContextProvider } from './contexts/SettingsContext';
import { TcfContextProvider } from './contexts/TcfContext';
import { UiContextProvider } from './contexts/UiContext';

// prevent the load of the UI from multiple script tags
const isRendered = window.UC_UI_IS_RENDERED || false;

if (isRendered) {
  throw new Error(ERROR_MESSAGES.DUPLICATED_SCRIPT);
}
window.UC_UI_IS_RENDERED = true;

interface GlobalUC {
  acceptAllConsents: () => Promise<void> | null;
  acceptService: (serviceId: string, consentType?: CONSENT_TYPE) => Promise<void>;
  acceptServices: (servicesIds: string[], consentType?: CONSENT_TYPE) => Promise<void>;
  areAllConsentsAccepted: () => boolean;
  clearStorage: () => Promise<void>;
  closeCMP: () => Promise<void>;
  denyAllConsents: () => Promise<void> | null;
  denyAndCloseCcpa: () => Promise<void>;
  enableScriptsForServicesWithConsent: () => void;
  getActiveLanguage: () => string;
  getServices: () => Service[];
  getServicesBaseInfo: () => BaseService[];
  getServicesFullInfo: () => Promise<Service[]>;
  getSettings: () => Settings;
  getTCFVendors: () => TCFVendor[] | undefined;
  isInitialized: () => boolean;
  rejectService: (serviceId: string, consentType?: CONSENT_TYPE) => Promise<void>;
  rejectServices: (servicesIds: string[], consentType?: CONSENT_TYPE) => Promise<void>;
  restartCMP: () => Promise<void>;
  showFirstLayer: () => Promise<void>;
  showSecondLayer: (scrollToServiceVendorId?: string | number) => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
}

declare global {
  interface Window {
    // eslint-disable-next-line camelcase
    __webpack_nonce__: string;
    UC_UI_IS_RENDERED: boolean;
    UC_UI_USER_COUNTRY_DATA: UserCountryData;
    UC_UI_USER_SESSION_DATA: UserSessionData;
    UC_UI_SUPPRESS_CMP_DISPLAY: boolean;
    [UC_UI_GLOBAL_NAMESPACE]?: GlobalUC;
    // Values supported by GTM Templates - refs EUD-1264
    [DATA_ATTRIBUTES.SETTINGS_ID]: string;
    [DATA_ATTRIBUTES.TCF_ENABLED]: boolean;
    [DATA_ATTRIBUTES.AMP_ENABLED]: boolean;
    [DATA_ATTRIBUTES.LANGUAGE]: string;
  }
}

let isUiInitialized = false;

type SdkProps = {
  initialUIValues: InitialUIValues;
  usercentrics: Usercentrics;
};

async function initSdk(initParameters: InitParameters): Promise<SdkProps> {
  const UC = new Usercentrics(initParameters.settingsId, { ...initParameters.options });

  const initialUIValues = await UC.init();

  return {
    initialUIValues,
    usercentrics: UC,
  };
}

interface UcContainers {
  appContainer: HTMLElement;
  rootContainer: HTMLElement;
}

function createUcRoot(): UcContainers {
  let rootContainer = document.getElementById(ROOT_ID);

  // if root element already exist, remove it
  if (rootContainer) {
    rootContainer.parentNode?.removeChild(rootContainer);
  }

  // First we create a new container with the 'usercentrics-root' id
  rootContainer = document.createElement('div');
  rootContainer.style.display = 'none';
  rootContainer.id = ROOT_ID;
  rootContainer.setAttribute('data-created-at', Date.now().toString());

  const appContainer = document.createElement('div');
  appContainer.setAttribute('data-nosnippet', '1');
  appContainer.setAttribute('data-testid', 'uc-app-container');

  // If ShadowDOM is supported by the current browser, then we want to display our UI inside a shadow root. The ShadowDOM
  // will act as a barrier from the host website's CSS and will also remove the unlikely possibility that our CSS will
  // change the host website. Older Browsers do not support ShadowDOM (e.g. Edge 17, IE11).
  if (
    typeof rootContainer.attachShadow === 'function' ||
    !!document.head.attachShadow ||
    !!HTMLElement.prototype.attachShadow
  ) {
    const shadowRoot = rootContainer.attachShadow({ mode: 'open' });
    shadowRoot.appendChild(appContainer);
  } else {
    rootContainer.appendChild(appContainer);
  }

  document.body.appendChild(rootContainer);

  isUiInitialized = true;

  return { appContainer, rootContainer };
}

async function initUi(): Promise<UcContainers> {
  return new Promise((resolve) => {
    if (document.readyState !== 'loading') {
      resolve(createUcRoot());
    } else {
      document.addEventListener('readystatechange', () => {
        if (!isUiInitialized) {
          resolve(createUcRoot());
        }
      });
    }
  });
}

type Props = {
  disableTracking: boolean;
  initialUIValues: InitialUIValues;
  target: UcContainers;
  restartCMP: () => Promise<void>;
  usercentrics: Usercentrics;
};

/** Creates the top-level preact component. */
const BrowserUI: FunctionalComponent<Props> = ({
  disableTracking,
  initialUIValues,
  restartCMP,
  target,
  usercentrics,
}) => {
  const [theme, setTheme] = useState(LightTheme);

  useEffect(() => {
    setTimeout(() => {
      const { rootContainer } = target;

      // Fixes content layout shift for now but we need to find a better solution for it
      if (rootContainer && rootContainer.style.display === 'none') {
        rootContainer.style.removeProperty('display');
      }
    }, 200);
  }, [target]);

  return (
    // The StylesheetManager is responsible for managing the styled-components css styles. The styles will be put inside
    // the target container. The disableCSSOMInjection makes sure that the CSS is added in text to the DOM and not
    // injected directly inside the CSS Object Model. By disabling this option, we can make sure that our CSS is added
    // inside the ShadowDOM and not globally. Global CSS would not pass through the shadow and thus our UI would not be
    // styled at all. As a trade-off to disabling this option, our CSS is processed slower.
    <StyleSheetManager target={target.appContainer} disableCSSOMInjection>
      <ThemeProvider theme={theme}>
        <GlobalStyles>
          <CustomCssProvider>
            <SettingsContextProvider>
              <CategoryContextProvider UC={usercentrics}>
                <TcfContextProvider>
                  <UiContextProvider disableTracking={disableTracking} UC={usercentrics}>
                    <GlobalStateContextProvider>
                      <App
                        onThemeChange={setTheme}
                        initialUIValues={initialUIValues}
                        restartCMP={restartCMP}
                        UC={usercentrics}
                      />
                    </GlobalStateContextProvider>
                  </UiContextProvider>
                </TcfContextProvider>
              </CategoryContextProvider>
            </SettingsContextProvider>
          </CustomCssProvider>
        </GlobalStyles>
      </ThemeProvider>
    </StyleSheetManager>
  );
};

export async function renderUI(): Promise<void> {
  /**
   * We read the customer's settingsId and further options from the script tag attribute and use the parameters to
   * initialize the BrowserSDK.
   */
  const initParameters = getInitParameters();
  const [sdk, ui] = await Promise.all([initSdk(initParameters), initUi()]);

  if (sdk.initialUIValues.ampEnabled) {
    // we need to init the amp ui for out virtual lists to work properly
    // eslint-disable-next-line no-console
    await sdk.usercentrics.uiReadyAmp().catch((error: Error) => console.warn(`AMP is not initialized: ${error}`));
  }

  const restartCMP = async () => {
    delete window[UC_UI_GLOBAL_NAMESPACE];
    await renderUI();
  };

  // We render the finished component inside our preactTarget (either shadow root or if shadow dom is not supported,
  // directly inside the div container we created)
  render(
    <BrowserUI
      disableTracking={initParameters.options.disableTracking || false}
      initialUIValues={sdk.initialUIValues}
      restartCMP={restartCMP}
      target={ui}
      usercentrics={sdk.usercentrics}
    />,
    ui.appContainer,
  );
}
